// src/pages/patient/RegisterKit.tsx

import React, { useEffect, useRef, useState } from "react";
import {
	registerKitWithPatientInfo,
	checkKitRegistration,
	updateKitRegistration,
	fetchKitPrefill,
	fetchKitRegistrationStatus,
	registerKitAuthenticated,
} from "../../api/healthcare/PatientApi";
import { requestPasswordResetByEmail } from "../../api/auth/authApi";
import { signIn, signOut } from "../../utils/auth/sessionUtilities";
import { fetchCustomTestPackagesPublic } from "../../api/healthcare/Testing/CustomTestPackageApi";
import { Select, Button, Input, Modal, Progress, Badge, Form, Row, Col, Typography, Switch, Alert } from "antd";
import type { InputRef } from "antd";
import { CheckCircleOutlined, WarningOutlined, UserOutlined, MailOutlined, PhoneOutlined, LockOutlined, ExperimentOutlined, SafetyOutlined, IdcardOutlined, LoginOutlined } from "@ant-design/icons";
import { CustomTestPackage } from "../../types/healthcare/TestTypes";
import { NeuralLoader } from "../../components/icons/NeuralLoader";
import { SectionCard } from "../../components/shared";
import { useNotifications } from "../../context/NotificationContext";
import { validateMedicareNumber } from "../../utils/healthcare/ValidateMedicareNumber";
import { normaliseMedicareNumberForApi } from "../../utils/healthcare/NormaliseMedicareNumber";
import {
	FAMILY_NAME_LABEL,
	GIVEN_NAME_LABEL,
	MIDDLE_NAME_LABEL,
	ONE_NAME_ONLY_LABEL,
	PATIENT_NAME_INVALID_MESSAGE,
	validatePatientPrimaryName,
} from "../healthcare/patient/patientNames/patientNameDisplay";

const { Option } = Select;
const { Title, Text } = Typography;

const useQuery = (): URLSearchParams => {
	return new URLSearchParams(window.location.search);
};

interface PatientFormData {
	first_name: string;
	middle_name?: string;
	last_name: string;
	has_one_name_only?: boolean;
	email: string;
	phone?: string;
	address?: string;
	city?: string;
	state?: string;
	country?: string;
	postal_code?: string;
	date_of_birth?: string;
	gender?: string;
	sex?: string;
	weight?: string;
	height?: string;
	password?: string;
	password_confirmation?: string;
	// CU-14ygtkjdwrp: no IHI is collected here, so no ihi_number key may be sent.
	patient_info?: {
		medicare_number?: string;
		medicare_individual_ref_number?: string;
		medicare_valid_to?: string;
	};
}

// CU-86d3vn0k0: a backend 422 on a name field maps to one fixed message so no
// raw submitted value or backend field text is echoed to the public visitor.
const NAME_FIELD_KEY_PATTERN = /(^|\.)(first_name|middle_name|last_name|has_one_name_only)$/;

const mapKitSubmissionError = (err: any, fallback: string): string => {
	if (err?.response?.status === 422) {
		const errorKeys = Object.keys(err.response?.data?.errors ?? {});
		if (errorKeys.some((key) => NAME_FIELD_KEY_PATTERN.test(key))) {
			return PATIENT_NAME_INVALID_MESSAGE;
		}
	}
	return err?.response?.data?.message || fallback;
};

const NON_PATIENT_ACCOUNT_MESSAGE = "This account isn't set up to activate kits. Please contact support.";
const MFA_SIGN_IN_MESSAGE = "This account uses two-step verification. Please sign in from the login page first, then return to this link.";
const KIT_ALREADY_REGISTERED_MESSAGE = "This kit is already registered. Log in to complete your registration";
const KIT_REGISTERED_TO_YOU_MESSAGE = "This kit is already registered to you.";
const SUPPORT_EMAIL = "info@mygene.com.au";
const SUPPORT_PHONE = "+61 434 048 778";
const ACCOUNT_EXISTS_CODE = "account_exists";
// K-L2: healthcare's fixed 409 for a kit registered to someone else that can no longer move; it names no owner.
const KIT_REGISTERED_ELSEWHERE_REASON = "kit_already_registered";
const KIT_REGISTERED_ELSEWHERE_MESSAGE = `This kit is registered to someone else. Please contact the clinic on ${SUPPORT_EMAIL} or ${SUPPORT_PHONE}.`;
// CU-14ygtkj75bk (HI_010_008526): Sex is a mandatory criterion of every permitted IHI search type, is never derived from gender,
// and is captured on the anonymous new-registration path only (the S2 update route takes no demographics).
const SEX_LABEL = "Sex";
const SEX_REQUIRED_MESSAGE = "Sex is required.";
const KIT_CHECK_FAILED_MESSAGE = `Please try again. If this keeps happening, contact us on ${SUPPORT_EMAIL} or ${SUPPORT_PHONE}.`;
const INVITATION_INTRO_MESSAGE = "If this kit is yours and you haven't set a password yet, or have forgotten it, we can email a link to set one to the address on the kit's account.";
const INVITATION_REQUESTED_MESSAGE = `If this kit's account can receive one, a link to set a password will be sent to the address on that account. It may take a few minutes and can land in junk or spam. If nothing arrives, contact us on ${SUPPORT_EMAIL} or ${SUPPORT_PHONE}.`;
const INVITATION_FAILED_MESSAGE = `We couldn't send that request. Please try again in a few minutes, or contact us on ${SUPPORT_EMAIL} or ${SUPPORT_PHONE}.`;

const isKitRegisteredElsewhere = (err: any): boolean =>
	err?.response?.status === 409 && err?.response?.data?.details?.reason === KIT_REGISTERED_ELSEWHERE_REASON;

const emptyFormData = (): PatientFormData => ({
	first_name: "",
	middle_name: "",
	last_name: "",
	has_one_name_only: false,
	email: "",
	phone: "",
	address: "",
	city: "",
	state: "",
	country: "Australia",
	postal_code: "",
	date_of_birth: "",
	gender: "",
	sex: "",
	weight: "",
	height: "",
	password: "",
	password_confirmation: "",
	patient_info: {
		medicare_number: "",
		medicare_individual_ref_number: "",
		medicare_valid_to: "",
	},
});

interface AuthenticatedAccount {
	first_name: string;
	last_name: string;
	email: string;
}

interface AccountExistsDialogProps {
	isOpen: boolean;
	email: string;
	showSharedAddressHelp: boolean;
	onLogin: () => void;
	onSharedAddress: () => void;
	onChangeEmail: () => void;
	onCancel: () => void;
}

// S9: renders only the address the visitor typed. The dialog it replaces echoed the
// existing account's name and phone to a caller who had proved nothing but that address.
const AccountExistsDialog: React.FC<AccountExistsDialogProps> = ({
	isOpen,
	email,
	showSharedAddressHelp,
	onLogin,
	onSharedAddress,
	onChangeEmail,
	onCancel,
}) => {
	return (
		<Modal
			title={
				<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
					<WarningOutlined style={{ color: "var(--mg-warning)", fontSize: 18 }} />
					<span>Account Already Exists</span>
				</div>
			}
			open={isOpen}
			onCancel={onCancel}
			// Focus is moved deliberately on close; the default restore would steal it back.
			focusTriggerAfterClose={false}
			footer={[
				<Button key="change" onClick={onChangeEmail} data-testid="kit-account-exists-change-email">
					Use a different email
				</Button>,
				<Button key="different" onClick={onSharedAddress} data-testid="kit-account-exists-different-person">
					This is a different person — create a new account
				</Button>,
				<Button key="login" type="primary" onClick={onLogin} data-testid="kit-account-exists-login">
					Log in and continue
				</Button>,
			]}
			destroyOnClose
		>
			<div style={{ padding: "16px 0" }} data-testid="kit-account-exists-body">
				<Text>
					An account already exists for <strong>{email}</strong>. Log in and we'll activate this kit under that account.
				</Text>

				{showSharedAddressHelp && (
					<div style={{ marginTop: 16 }} data-testid="kit-account-exists-shared-address">
						<Alert
							type="info"
							showIcon
							message="Setting up a second account on a shared email address"
							description={`Family members often share one inbox, so this may not be your account. We can't set up a second account on a shared address from this page — please contact our team on ${SUPPORT_EMAIL} or ${SUPPORT_PHONE} and we'll create the new account and activate this kit for you.`}
						/>
					</div>
				)}
			</div>
		</Modal>
	);
};

// CU-14ygtkjdr83: Healthcare resolves the kit's owner itself and answers identically either way, so this sends the sample id alone and claims no outcome.
const RegisteredKitInvitation: React.FC<{ sampleId: string }> = ({ sampleId }) => {
	const [isSending, setIsSending] = useState(false);
	const [isRequested, setIsRequested] = useState(false);
	const [requestFailed, setRequestFailed] = useState(false);

	const requestInvitation = async () => {
		setIsSending(true);
		setRequestFailed(false);
		try {
			await updateKitRegistration(sampleId);
			setIsRequested(true);
		} catch {
			setRequestFailed(true);
		} finally {
			setIsSending(false);
		}
	};

	return (
		<SectionCard title="Need to set your password?">
			<div data-testid="kit-invitation">
				<Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
					{INVITATION_INTRO_MESSAGE}
				</Text>
				{isRequested ? (
					<Alert
						type="info"
						showIcon
						message="Request received"
						description={INVITATION_REQUESTED_MESSAGE}
						data-testid="kit-invitation-requested"
					/>
				) : (
					<>
						{requestFailed && (
							<Alert type="error" showIcon message={INVITATION_FAILED_MESSAGE} style={{ marginBottom: 16 }} data-testid="kit-invitation-error" />
						)}
						<Button
							type="primary"
							icon={<MailOutlined />}
							onClick={requestInvitation}
							loading={isSending}
							disabled={isSending}
							data-testid="kit-invitation-submit"
						>
							Email a set-password link
						</Button>
					</>
				)}
			</div>
		</SectionCard>
	);
};

const RegisterKit: React.FC = () => {
	const query = useQuery();
	const sampleId = query.get("id");
	const { showSuccess, showError } = useNotifications();

	const [isKitRegistered, setIsKitRegistered] = useState(false);
	const [isCheckingKit, setIsCheckingKit] = useState(true);
	const [kitCheckFailed, setKitCheckFailed] = useState(false);
	const [kitCheckAttempt, setKitCheckAttempt] = useState(0);

	const [formData, setFormData] = useState<PatientFormData>(emptyFormData);

	const [selectedPrograms, setSelectedPrograms] = useState<string[]>([]);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isSubmitted, setIsSubmitted] = useState(false);
	// An existing customer is promoted and invited by email; the password typed here is not applied (contract §7.1).
	const [invitedExistingAccount, setInvitedExistingAccount] = useState(false);
	const [error, setError] = useState<string>("");
	const [kitRegisteredElsewhere, setKitRegisteredElsewhere] = useState(false);
	const [isImagePopupOpen, setIsImagePopupOpen] = useState(false);
	const [showAccountExists, setShowAccountExists] = useState(false);
	const [accountExistsEmail, setAccountExistsEmail] = useState("");
	const [showSharedAddressHelp, setShowSharedAddressHelp] = useState(false);
	const [showWelcome, setShowWelcome] = useState(true);
	const [welcomePhase, setWelcomePhase] = useState(0);
	const [currentProgress, setCurrentProgress] = useState(0);

	const [customPackages, setCustomPackages] = useState<CustomTestPackage[]>([]);
	const [loadingPackages, setLoadingPackages] = useState(false);

	// Never inferred from an existing cookie: a practitioner signed in on a clinic
	// device would otherwise bind a patient's kit to their own account.
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [authAccount, setAuthAccount] = useState<AuthenticatedAccount | null>(null);
	const [loginUsername, setLoginUsername] = useState("");
	const [loginPassword, setLoginPassword] = useState("");
	const [loginError, setLoginError] = useState("");
	const [isLoggingIn, setIsLoggingIn] = useState(false);

	// S9: "mine" only ever comes from the identified route returning the caller's own prefill block.
	const [kitOwnership, setKitOwnership] = useState<"unknown" | "mine" | "other">("unknown");

	const emailInputRef = useRef<InputRef>(null);
	const loginCardRef = useRef<HTMLDivElement>(null);
	const loginUsernameRef = useRef<InputRef>(null);

	const [isForgotOpen, setIsForgotOpen] = useState(false);
	const [forgotEmail, setForgotEmail] = useState("");
	const [forgotError, setForgotError] = useState("");
	const [forgotMessage, setForgotMessage] = useState("");
	const [isSendingReset, setIsSendingReset] = useState(false);

	useEffect(() => {
		const checkKit = async () => {
			if (!sampleId) {
				setIsCheckingKit(false);
				setShowWelcome(false);
				return;
			}

			try {
				const response = await checkKitRegistration(sampleId);

				if (response?.success !== true) {
					setKitCheckFailed(true);
					setShowWelcome(false);
					return;
				}

				// CU-14ygtkjdr83: a kit that exists with no owner is still an ordinary new registration.
				setIsKitRegistered(response.registered === true);

				setTimeout(() => setWelcomePhase(1), 800);
				setTimeout(() => setWelcomePhase(2), 1800);
				setTimeout(() => setWelcomePhase(3), 2800);
				setTimeout(() => {
					setShowWelcome(false);
					setCurrentProgress(33);
				}, 4200);
			} catch (err: any) {
				console.error("Error checking kit registration:", err);
				setKitCheckFailed(true);
				setShowWelcome(false);
			} finally {
				setIsCheckingKit(false);
			}
		};

		checkKit();
	}, [sampleId, kitCheckAttempt]);

	useEffect(() => {
		const loadCustomPackages = async () => {
			setLoadingPackages(true);
			try {
				const packages = await fetchCustomTestPackagesPublic();
				setCustomPackages(packages.filter((pkg: CustomTestPackage) => pkg.is_active));
			} catch (error) {
				console.error("Failed to load custom test packages:", error);
				showError("Failed to load available test packages");
			} finally {
				setLoadingPackages(false);
			}
		};

		loadCustomPackages();
	}, []);

	useEffect(() => {
		if (!isAuthenticated || !isKitRegistered || !sampleId) return;

		let cancelled = false;
		fetchKitRegistrationStatus(sampleId)
			.then((status) => {
				if (!cancelled) setKitOwnership(status?.patient ? "mine" : "other");
			})
			.catch(() => {
				if (!cancelled) setKitOwnership("other");
			});

		return () => {
			cancelled = true;
		};
	}, [isAuthenticated, isKitRegistered, sampleId]);

	const handleInputChange = (field: string, value: any) => {
		if (field.startsWith("patient_info.")) {
			const fieldName = field.replace("patient_info.", "");
			setFormData((prev) => ({
				...prev,
				patient_info: {
					...prev.patient_info,
					[fieldName]: value || "",
				},
			}));
		} else {
			setFormData((prev) => ({
				...prev,
				[field]: value || "",
			}));
		}
	};

	const handleProgramChange = (programs: string[]) => {
		setSelectedPrograms(programs);
	};

	// CU-86d3vn0k0: mirrors the authenticated capture — the single name lives in the
	// family-name role.
	const handleOneNameOnly = (checked: boolean) => {
		setFormData((prev) => ({
			...prev,
			has_one_name_only: checked,
			...(checked ? { first_name: "", middle_name: "" } : {}),
		}));
	};

	const clearPageSession = () => {
		void signOut();
		setIsAuthenticated(false);
		setAuthAccount(null);
		setKitOwnership("unknown");
		setKitRegisteredElsewhere(false);
	};

	const applyPrefill = async () => {
		const prefill = await fetchKitPrefill();
		setFormData((prev) => ({
			...prev,
			password: "",
			password_confirmation: "",
			first_name: prefill.first_name || "",
			middle_name: prefill.middle_name || "",
			last_name: prefill.last_name || "",
			has_one_name_only: prefill.has_one_name_only === true,
			email: prefill.email || "",
			phone: prefill.phone || "",
			address: prefill.address || "",
			city: prefill.city || "",
			state: prefill.state || "",
			country: prefill.country || "Australia",
			postal_code: prefill.postal_code || "",
			date_of_birth: prefill.date_of_birth || "",
			gender: prefill.gender || "",
			weight: prefill.weight || "",
			height: prefill.height || "",
			patient_info: {
				...prev.patient_info,
				medicare_number: prefill.patient_info?.medicare_number || "",
			},
		}));
	};

	const handleLogin = async () => {
		setLoginError("");
		setError("");
		setIsLoggingIn(true);

		try {
			const result = await signIn(loginUsername, loginPassword);
			// Both continuations (second factor, confined enrolment) live on the login page.
			if (result.status !== "authenticated") {
				setLoginError(MFA_SIGN_IN_MESSAGE);
				return;
			}
			const user = result.user;
			setAuthAccount({
				first_name: user?.first_name || "",
				last_name: user?.last_name || "",
				email: user?.email || "",
			});
			setIsAuthenticated(true);
			// The refusal was for the anonymous attempt; the kit's own patient may be the one signing in.
			setKitRegisteredElsewhere(false);

			try {
				await applyPrefill();
			} catch (prefillErr: any) {
				clearPageSession();
				setFormData(emptyFormData());
				setLoginError(
					prefillErr?.response?.status === 403
						? NON_PATIENT_ACCOUNT_MESSAGE
						: prefillErr?.response?.data?.message || "We could not load your details. Please try again."
				);
			}
		} catch (err: any) {
			setLoginError(err?.response?.data?.message || "Something has gone wrong, please try again");
		} finally {
			setIsLoggingIn(false);
		}
	};

	const handleSignOut = () => {
		clearPageSession();
		setFormData(emptyFormData());
		setLoginUsername("");
		setLoginPassword("");
		setLoginError("");
		setError("");
	};

	const handleForgotSubmit = async () => {
		setIsSendingReset(true);
		setForgotError("");
		setForgotMessage("");

		try {
			await requestPasswordResetByEmail(forgotEmail);
			setForgotMessage("If that address matches an account, we've emailed a reset link with your username.");
			setForgotEmail("");
		} catch (err: any) {
			setForgotError(err?.response?.data?.message || "Unable to process that request. Please try again.");
		} finally {
			setIsSendingReset(false);
		}
	};

	const openForgotModal = () => {
		setForgotError("");
		setForgotMessage("");
		setIsForgotOpen(true);
	};

	const closeForgotModal = () => {
		setIsForgotOpen(false);
		setForgotError("");
		setForgotMessage("");
	};

	const handleSubmit = async (e?: React.FormEvent) => {
		if (e) e.preventDefault();
		if (!sampleId) {
			setError("Sample ID is required");
			return;
		}

		setError("");

		// Medicare is optional. When a value is supplied it must pass the shared
		// Appendix D helper before we attempt submission; the backend remains the
		// authoritative persistence guard.
		const medicareInput = formData.patient_info?.medicare_number ?? "";
		if (medicareInput.trim() !== "") {
			const medicareCheck = validateMedicareNumber(medicareInput);
			if (!medicareCheck.isValid) {
				setError(medicareCheck.error || "Please enter a valid Medicare card number.");
				return;
			}
		}

		// The signed-in path sends no name component at all, so there is nothing here to validate.
		if (!isAuthenticated) {
			const nameErrors = validatePatientPrimaryName(formData);
			if (nameErrors.length > 0) {
				setError(nameErrors[0]);
				return;
			}
			if (!isKitRegistered && (formData.sex ?? "").trim() === "") {
				setError(SEX_REQUIRED_MESSAGE);
				return;
			}
		}

		setCurrentProgress(66);

		if (isAuthenticated) {
			await submitAuthenticatedKit();
			return;
		}

		await submitForm();
	};

	const submitAuthenticatedKit = async () => {
		setIsSubmitting(true);
		setError("");
		setCurrentProgress(100);

		const notes = selectedPrograms.join(", ");
		const selectedPackageIds = customPackages
			.filter((pkg) => selectedPrograms.includes(pkg.name))
			.map((pkg) => pkg.id);

		// Normalise a copied payload (continuous digits for the API) without
		// rewriting the value the user still sees in the form.
		const submissionData: PatientFormData = {
			...formData,
			patient_info: {
				...formData.patient_info!,
				medicare_number: normaliseMedicareNumberForApi(formData.patient_info?.medicare_number ?? ""),
			},
		};

		try {
			await registerKitAuthenticated(sampleId!, submissionData, notes, selectedPackageIds);
			setIsSubmitted(true);
			showSuccess("Kit activated successfully!");
		} catch (err: any) {
			if (isKitRegisteredElsewhere(err)) {
				setKitRegisteredElsewhere(true);
				setCurrentProgress(33);
				return;
			}
			setError(mapKitSubmissionError(err, "An error occurred while activating your kit"));
			showError("Failed to activate kit");
		} finally {
			setIsSubmitting(false);
		}
	};

	const submitForm = async () => {
		setIsSubmitting(true);
		setError("");
		setCurrentProgress(100);

		// Normalise a copied payload (continuous digits for the API) without
		// rewriting the value the user still sees in the form.
		const submissionData: PatientFormData = {
			...formData,
			patient_info: {
				...formData.patient_info!,
				medicare_number: normaliseMedicareNumberForApi(formData.patient_info?.medicare_number ?? ""),
			},
		};

		const notes = selectedPrograms.join(", ");
		// The selected packages are the source of truth for test types; send their
		// ids so the backend resolves types from each package's analysis_types.
		const selectedPackageIds = customPackages
			.filter((pkg) => selectedPrograms.includes(pkg.name))
			.map((pkg) => pkg.id);

		try {
			const result = await registerKitWithPatientInfo(sampleId!, submissionData, notes, selectedPackageIds);
			setInvitedExistingAccount(result?.data?.was_existing_user === true);
			setIsSubmitted(true);
			showSuccess("Kit activated successfully!");
		} catch (err: any) {
			if (isKitRegisteredElsewhere(err)) {
				setKitRegisteredElsewhere(true);
				setCurrentProgress(33);
				return;
			}
			if (err?.response?.status === 409 && err?.response?.data?.code === ACCOUNT_EXISTS_CODE) {
				setAccountExistsEmail(formData.email);
				setShowSharedAddressHelp(false);
				setShowAccountExists(true);
				setCurrentProgress(33);
				return;
			}
			setError(mapKitSubmissionError(err, "An error occurred while activating your kit"));
			showError("Failed to activate kit");
		} finally {
			setIsSubmitting(false);
		}
	};

	const closeAccountExists = () => {
		setShowAccountExists(false);
		setShowSharedAddressHelp(false);
	};

	const handleGoToLogin = () => {
		closeAccountExists();
		loginCardRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
		loginUsernameRef.current?.focus();
	};

	const handleChangeEmail = () => {
		closeAccountExists();
		emailInputRef.current?.focus();
		emailInputRef.current?.select?.();
	};

	const retryKitCheck = () => {
		setKitCheckFailed(false);
		setIsCheckingKit(true);
		setKitCheckAttempt((attempt) => attempt + 1);
	};

	const openImagePopup = () => setIsImagePopupOpen(true);
	const closeImagePopup = () => setIsImagePopupOpen(false);

	if (!sampleId) {
		return (
			<div style={{ minHeight: "100vh", background: "var(--mg-bg, #fafafa)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
				<div style={{ maxWidth: 400, width: "100%", textAlign: "center" }}>
					<SectionCard>
						<div style={{ padding: "24px 0" }}>
							<WarningOutlined style={{ fontSize: 56, color: "var(--mg-warning)" }} />
							<Title level={3} style={{ marginTop: 16, marginBottom: 8 }}>Invalid Link</Title>
							<Text type="secondary" style={{ fontSize: 15 }}>
								This activation link appears to be invalid. Please check your link and try again.
							</Text>
						</div>
					</SectionCard>
				</div>
			</div>
		);
	}

	if (isCheckingKit || showWelcome) {
		return (
			<div style={{ minHeight: "100vh", position: "relative" }}>
				<NeuralLoader size="full" text="" fullscreen={true} backgroundColor="black" networkColor="white" />
				<div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60 }}>
					<div style={{ textAlign: "center", maxWidth: 600, padding: "0 24px" }}>
						{welcomePhase >= 1 && (
							<div className="rk-slide-up-delayed">
								<h1 style={{ fontSize: 52, fontWeight: 700, color: "white", marginBottom: 16, lineHeight: 1.2 }}>Welcome to MyGene</h1>
							</div>
						)}
						{welcomePhase >= 2 && (
							<div className="rk-slide-up-super-delayed">
								<p style={{ fontSize: 18, color: "rgba(255,255,255,0.9)", lineHeight: 1.6 }}>
									Let's activate your DNA Collection Kit and begin your personalized health journey
								</p>
							</div>
						)}
						{welcomePhase >= 3 && (
							<div className="rk-fade-in-final" style={{ marginTop: 32 }}>
								<div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8, color: "rgba(255,255,255,0.8)", fontSize: 16 }}>
									<span>Preparing your activation form</span>
									<SafetyOutlined />
								</div>
								<div style={{ marginTop: 16, maxWidth: 300, margin: "16px auto 0" }}>
									<Progress percent={100} strokeColor="#ffffff" showInfo={false} />
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		);
	}

	if (kitCheckFailed) {
		return (
			<div style={{ minHeight: "100vh", background: "var(--mg-bg, #fafafa)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
				<div style={{ maxWidth: 400, width: "100%", textAlign: "center" }} data-testid="kit-check-failed">
					<SectionCard>
						<div style={{ padding: "24px 0" }}>
							<WarningOutlined style={{ fontSize: 56, color: "var(--mg-warning)" }} />
							<Title level={3} style={{ marginTop: 16, marginBottom: 8 }}>We couldn't check this kit</Title>
							<Text type="secondary" style={{ fontSize: 15, display: "block", marginBottom: 24 }}>
								{KIT_CHECK_FAILED_MESSAGE}
							</Text>
							<Button type="primary" onClick={retryKitCheck} data-testid="kit-check-retry">
								Try again
							</Button>
						</div>
					</SectionCard>
				</div>
			</div>
		);
	}

	if (isSubmitted) {
		return (
			<div style={{ minHeight: "100vh", background: "var(--mg-bg, #fafafa)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
				<div style={{ maxWidth: 560, width: "100%" }}>
					<SectionCard>
						<div style={{ textAlign: "center", padding: "24px 0" }}>
							<CheckCircleOutlined style={{ fontSize: 72, color: "var(--mg-success, #52c41a)" }} />
							<Title level={2} style={{ marginTop: 16, marginBottom: 8 }}>
								{isKitRegistered ? "Registration Completed! 🎉" : "Kit Activated Successfully! 🎉"}
							</Title>
							<Text type="secondary" style={{ fontSize: 15, lineHeight: 1.6 }}>
								{isKitRegistered
									? "Your account has been set up and is ready to use. You can now log in with your credentials."
									: "Your DNA Collection Kit has been activated and your profile has been created."}
							</Text>
							{invitedExistingAccount && (
								<Alert
									data-testid="kit-existing-account-invited"
									type="info"
									showIcon
									style={{ marginTop: 16, textAlign: "left" }}
									message="Check your email to set your password"
									description="This email address already had an account, so the kit was added to it. We have emailed you a link to choose your password; the password you entered here was not used."
								/>
							)}

							<div style={{
								background: "var(--mg-bg-subtle, #fafafa)",
								border: "1px solid var(--mg-border, #e8e8e8)",
								borderRadius: 8,
								padding: "16px 24px",
								margin: "24px 0",
							}}>
								<div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 8 }}>
									<ExperimentOutlined />
									<Text strong>Kit ID:</Text>
								</div>
								<Text code style={{ fontSize: 18 }}>{sampleId}</Text>
							</div>

							<div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "center", marginBottom: 16 }}>
								<Button type="default" onClick={openImagePopup} icon={<ExperimentOutlined />}>
									View Kit Instructions
								</Button>
								{isAuthenticated && (
									<Button type="primary" href="/patient" data-testid="kit-go-to-account">
										Go to my account
									</Button>
								)}
							</div>

							<div style={{ marginTop: 8 }}>
								<Text type="secondary" style={{ fontSize: 13 }}>Next steps will be sent to your email</Text>
							</div>
						</div>
					</SectionCard>
				</div>

				<Modal title="Kit Instructions" open={isImagePopupOpen} onCancel={closeImagePopup} footer={null} width={800} destroyOnClose>
					<img src="/assets/images/Mygene-DNA-Kit-Instructions.jpeg" alt="DNA Kit Instructions" style={{ maxWidth: "100%", height: "auto", borderRadius: 8 }} />
					<Text type="secondary" style={{ display: "block", textAlign: "center", marginTop: 16 }}>
						Follow these step-by-step instructions to properly collect your DNA sample.
					</Text>
				</Modal>
			</div>
		);
	}

	const isOneNameOnly = formData.has_one_name_only === true;
	const isAnonymousRegisteredKit = isKitRegistered && !isAuthenticated;

	// Optional Medicare field: validate live only when a value is present so a
	// blank entry stays allowed. Drives the inline message and submit guard.
	const medicareNumberInput = formData.patient_info?.medicare_number ?? "";
	const medicareNumberError =
		medicareNumberInput.trim() === ""
			? undefined
			: validateMedicareNumber(medicareNumberInput).error;

	return (
		<div style={{ minHeight: "100vh", background: "var(--mg-bg, #fafafa)" }}>
			<AccountExistsDialog
				isOpen={showAccountExists}
				email={accountExistsEmail}
				showSharedAddressHelp={showSharedAddressHelp}
				onLogin={handleGoToLogin}
				onSharedAddress={() => setShowSharedAddressHelp(true)}
				onChangeEmail={handleChangeEmail}
				onCancel={closeAccountExists}
			/>

			{/* Sticky header */}
			<div style={{
				background: "var(--mg-bg-elevated, #fff)",
				borderBottom: "1px solid var(--mg-border, #e8e8e8)",
				position: "sticky",
				top: 0,
				zIndex: 40,
				boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
			}}>
				<div style={{ maxWidth: 960, margin: "0 auto", padding: "16px 24px" }}>
					<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
						<img src="/assets/images/logo/logoBlack.png" alt="MyGene Logo" style={{ maxWidth: 160, height: "auto" }} />
						<div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
							<Text type="secondary" style={{ fontSize: 13 }}>
								<MailOutlined style={{ marginRight: 6 }} />
								{SUPPORT_EMAIL}
							</Text>
							<Text type="secondary" style={{ fontSize: 13 }}>
								<PhoneOutlined style={{ marginRight: 6 }} />
								{SUPPORT_PHONE}
							</Text>
						</div>
					</div>
				</div>
			</div>

			{/* Main content */}
			<div style={{ maxWidth: 960, margin: "0 auto", padding: "48px 24px" }} data-testid="kit-registration-form">

				{/* Hero */}
				<div style={{ textAlign: "center", marginBottom: 32 }}>
					<Title level={2} style={{ color: "var(--mg-text, #262626)", marginBottom: 12 }}>
						{isKitRegistered ? "Complete Your Kit Registration" : "Activate Your DNA Collection Kit"}
					</Title>
					<Text type="secondary" style={{ fontSize: 15, lineHeight: 1.6 }}>
						{isAnonymousRegisteredKit
							? "Log in to continue, or request a link to set your password."
							: isKitRegistered
								? "Please complete your registration by filling in any missing information and setting up your password"
								: "Please enter your details below to activate your DNA Collection Kit"}
					</Text>
					<div style={{
						display: "inline-flex",
						alignItems: "center",
						gap: 8,
						background: "var(--mg-bg-subtle, #f5f5f5)",
						border: "1px solid var(--mg-border, #e8e8e8)",
						borderRadius: 100,
						padding: "8px 20px",
						marginTop: 16,
					}}>
						<Badge status="processing" />
						<Text strong>Kit ID:</Text>
						<Text code style={{ fontSize: 14 }}>{sampleId}</Text>
					</div>
				</div>

				{/* Sign-in decision — the first choice the visitor makes, so it sits directly under the hero */}
				{!isAuthenticated && (
					<div ref={loginCardRef} style={{ marginBottom: 16 }} data-testid="kit-login-card">
						<SectionCard title="Already have a MyGene account?">
							<Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
								Log in and we'll fill this form in for you.
							</Text>

							{isKitRegistered && (
								<Alert
									type="info"
									showIcon
									message={KIT_ALREADY_REGISTERED_MESSAGE}
									style={{ marginBottom: 16 }}
								/>
							)}

							{loginError && (
								<Alert type="error" showIcon message={loginError} style={{ marginBottom: 16 }} data-testid="kit-login-error" />
							)}

							<Form layout="vertical" onFinish={handleLogin}>
								<Row gutter={[16, 0]}>
									<Col xs={24} md={12}>
										<Form.Item label="Username" help="Use the username from your kit registration email — not your email address.">
											<Input
												ref={loginUsernameRef}
												prefix={<UserOutlined style={{ color: "var(--mg-text-disabled, #bfbfbf)" }} />}
												aria-label="Username"
												autoComplete="username"
												value={loginUsername}
												onChange={(e) => setLoginUsername(e.target.value)}
												data-testid="kit-login-username"
											/>
										</Form.Item>
									</Col>
									<Col xs={24} md={12}>
										<Form.Item label="Password">
											<Input.Password
												prefix={<LockOutlined style={{ color: "var(--mg-text-disabled, #bfbfbf)" }} />}
												aria-label="Account password"
												autoComplete="current-password"
												value={loginPassword}
												onChange={(e) => setLoginPassword(e.target.value)}
												data-testid="kit-login-password"
											/>
										</Form.Item>
									</Col>
								</Row>
								<div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
									<Button
										type="primary"
										htmlType="submit"
										icon={<LoginOutlined />}
										loading={isLoggingIn}
										disabled={isLoggingIn}
										data-testid="kit-login-submit"
									>
										Log in
									</Button>
									<Button type="link" onClick={openForgotModal} style={{ padding: 0 }} data-testid="kit-forgot-link">
										Forgot your username or password?
									</Button>
								</div>
							</Form>

							{!isKitRegistered && (
								<Text type="secondary" style={{ fontSize: 13, display: "block", marginTop: 16 }}>
									No account yet? Just fill in the form below to create one.
								</Text>
							)}
						</SectionCard>
					</div>
				)}

				{isAuthenticated && authAccount && (
					<div style={{ marginBottom: 16 }} data-testid="kit-auth-banner">
						<Alert
							type="success"
							showIcon
							message={`Activating as ${authAccount.first_name} ${authAccount.last_name} (${authAccount.email})`}
							action={
								<Button size="small" onClick={handleSignOut} data-testid="kit-sign-out">
									Not you? Sign out
								</Button>
							}
						/>
					</div>
				)}

				{isAuthenticated && isKitRegistered && (
					<div style={{ marginBottom: 16 }}>
						<Alert
							type={kitOwnership === "mine" ? "success" : "info"}
							showIcon
							message={kitOwnership === "mine" ? KIT_REGISTERED_TO_YOU_MESSAGE : KIT_ALREADY_REGISTERED_MESSAGE}
							data-testid={kitOwnership === "mine" ? "kit-registered-to-you" : "kit-registered-generic"}
						/>
					</div>
				)}

				{/* Kit Instructions CTA */}
				<div style={{ textAlign: "center", marginBottom: 24 }}>
					<Button type="default" onClick={openImagePopup} icon={<ExperimentOutlined />}>
						View Kit Instructions
					</Button>
					<div style={{ marginTop: 12 }}>
						<Text type="secondary" style={{ fontSize: 13, display: "block" }}>
							Please ensure when using the swab, to push firmly on the side of your mouth
						</Text>
						<Text style={{ fontSize: 13, color: "var(--mg-warning, #d46b08)" }}>
							⚠️ Re-collection fees will apply for insufficient DNA yield due to improper swabbing technique.
						</Text>
					</div>
				</div>

				{/* Privacy notice */}
				<div style={{ marginBottom: 16 }}>
					<SectionCard>
						<div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
							<SafetyOutlined style={{ fontSize: 22, color: "var(--mg-primary, #1890ff)", flexShrink: 0, marginTop: 2 }} />
							<div>
								<Text strong style={{ fontSize: 14, display: "block", marginBottom: 6 }}>Your Privacy Matters</Text>
								<Text type="secondary" style={{ lineHeight: 1.6 }}>
									We protect your personal information in accordance with the Australian Privacy Act. Your genetic data is securely stored and never shared without your explicit consent.
								</Text>
								<br />
								<a
									href="https://mygene.com.au/pages/about-mygene-the-legalities"
									target="_blank"
									rel="noopener noreferrer"
									style={{ color: "var(--mg-primary, #1890ff)", fontWeight: 500 }}
								>
									Read our full Privacy Policy →
								</a>
							</div>
						</div>
					</SectionCard>
				</div>

				{kitRegisteredElsewhere && (
					<Alert
						type="warning"
						showIcon
						message={KIT_REGISTERED_ELSEWHERE_MESSAGE}
						style={{ marginBottom: 16 }}
						data-testid="kit-registered-elsewhere"
					/>
				)}

				{/* Error alert */}
				{error && (
					<div style={{
						marginBottom: 16,
						padding: "12px 16px",
						background: "var(--mg-danger-bg, #fff2f0)",
						border: "1px solid var(--mg-danger, #ff4d4f)",
						borderRadius: 8,
					}}>
						<div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
							<WarningOutlined style={{ color: "var(--mg-danger, #ff4d4f)", marginTop: 2, flexShrink: 0 }} />
							<div>
								<Text strong style={{ color: "var(--mg-danger, #ff4d4f)", display: "block" }}>Error</Text>
								<Text style={{ color: "var(--mg-danger, #ff4d4f)" }}>{error}</Text>
							</div>
						</div>
					</div>
				)}

				{isAnonymousRegisteredKit ? (
					<RegisteredKitInvitation sampleId={sampleId} />
				) : (
					<>
						{/* Form sections */}
						<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

							{/* Personal details */}
							<SectionCard title={isKitRegistered ? "Your Information" : "Personal Details"}>
								<Form layout="vertical">
									<Row gutter={[16, 0]}>
										{isAuthenticated && (
											<Col xs={24}>
												<Text type="secondary" style={{ fontSize: 13, display: "block", marginBottom: 12 }}>
													To change your name, contact support.
												</Text>
											</Col>
										)}
										<Col xs={24}>
											<Form.Item label={ONE_NAME_ONLY_LABEL} style={{ marginBottom: 8 }}>
												<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
													<Switch aria-label={ONE_NAME_ONLY_LABEL} checked={isOneNameOnly} disabled={isAuthenticated} onChange={handleOneNameOnly} />
													<Text type="secondary" style={{ fontSize: 13 }}>
														If you have a single name, record it in the family name field.
													</Text>
												</div>
											</Form.Item>
										</Col>
										<Col xs={24} md={12}>
											<Form.Item label={isOneNameOnly ? GIVEN_NAME_LABEL : <>{GIVEN_NAME_LABEL} <span style={{ color: "var(--mg-danger, #ff4d4f)" }}>*</span></>}>
												<Input
													prefix={<UserOutlined style={{ color: "var(--mg-text-disabled, #bfbfbf)" }} />}
													aria-label={GIVEN_NAME_LABEL}
													disabled={isOneNameOnly || isAuthenticated}
													value={isOneNameOnly ? "" : formData.first_name}
													onChange={(e) => handleInputChange("first_name", e.target.value)}
													data-testid="kit-firstname"
												/>
											</Form.Item>
										</Col>
										<Col xs={24} md={12}>
											<Form.Item label={<>{FAMILY_NAME_LABEL} <span style={{ color: "var(--mg-danger, #ff4d4f)" }}>*</span></>}>
												<Input
													prefix={<UserOutlined style={{ color: "var(--mg-text-disabled, #bfbfbf)" }} />}
													aria-label={FAMILY_NAME_LABEL}
													disabled={isAuthenticated}
													value={formData.last_name}
													placeholder={isOneNameOnly ? "Your single recorded name" : undefined}
													onChange={(e) => handleInputChange("last_name", e.target.value)}
												/>
											</Form.Item>
										</Col>
										<Col xs={24} md={12}>
											<Form.Item label={MIDDLE_NAME_LABEL}>
												<Input
													prefix={<UserOutlined style={{ color: "var(--mg-text-disabled, #bfbfbf)" }} />}
													aria-label={MIDDLE_NAME_LABEL}
													disabled={isOneNameOnly || isAuthenticated}
													value={isOneNameOnly ? "" : formData.middle_name || ""}
													onChange={(e) => handleInputChange("middle_name", e.target.value)}
												/>
											</Form.Item>
										</Col>
										<Col xs={24} md={12}>
											<Form.Item label={<>Email Address <span style={{ color: "var(--mg-danger, #ff4d4f)" }}>*</span></>}>
												<Input
													ref={emailInputRef}
													prefix={<MailOutlined style={{ color: "var(--mg-text-disabled, #bfbfbf)" }} />}
													type="email"
													aria-label="Email Address"
													disabled={isAuthenticated}
													value={formData.email}
													onChange={(e) => handleInputChange("email", e.target.value)}
												/>
											</Form.Item>
										</Col>
										<Col xs={24} md={12}>
											<Form.Item label="Phone Number">
												<Input
													prefix={<PhoneOutlined style={{ color: "var(--mg-text-disabled, #bfbfbf)" }} />}
													type="tel"
													value={formData.phone || ""}
													onChange={(e) => handleInputChange("phone", e.target.value)}
												/>
											</Form.Item>
										</Col>
										<Col xs={24} md={12}>
											<Form.Item label={<>Date of Birth <span style={{ color: "var(--mg-danger, #ff4d4f)" }}>*</span></>}>
												<Input
													type="date"
													value={formData.date_of_birth || ""}
													onChange={(e) => handleInputChange("date_of_birth", e.target.value)}
												/>
											</Form.Item>
										</Col>
										<Col xs={24} md={12}>
											<Form.Item label="Gender">
												<Select
													value={formData.gender || undefined}
													onChange={(value) => handleInputChange("gender", value)}
													placeholder="Select Gender"
													style={{ width: "100%" }}
												>
													<Option value="Male">Male</Option>
													<Option value="Female">Female</Option>
													<Option value="Other">Other</Option>
												</Select>
											</Form.Item>
										</Col>
										{!isAuthenticated && !isKitRegistered && (
											<Col xs={24} md={12}>
												<Form.Item label={<>{SEX_LABEL} <span style={{ color: "var(--mg-danger, #ff4d4f)" }}>*</span></>}>
													<Input
														aria-label={SEX_LABEL}
														maxLength={10}
														value={formData.sex || ""}
														onChange={(e) => handleInputChange("sex", e.target.value)}
														placeholder="Male / Female"
														data-testid="kit-sex"
													/>
												</Form.Item>
											</Col>
										)}
										<Col xs={24} md={6}>
											<Form.Item label="Weight (kg)">
												<Input
													value={formData.weight || ""}
													onChange={(e) => handleInputChange("weight", e.target.value)}
													placeholder="e.g. 70"
												/>
											</Form.Item>
										</Col>
										<Col xs={24} md={6}>
											<Form.Item label="Height (cm)">
												<Input
													value={formData.height || ""}
													onChange={(e) => handleInputChange("height", e.target.value)}
													placeholder="e.g. 175"
												/>
											</Form.Item>
										</Col>
										<Col xs={24} md={12}>
											<Form.Item
												label="Medicare Card Number"
												validateStatus={medicareNumberError ? "error" : undefined}
												help={medicareNumberError}
											>
												<Input
													prefix={<IdcardOutlined style={{ color: "var(--mg-text-disabled, #bfbfbf)" }} />}
													inputMode="numeric"
													value={formData.patient_info?.medicare_number || ""}
													onChange={(e) => handleInputChange("patient_info.medicare_number", e.target.value)}
													placeholder="Optional — e.g. 2123 45670 1"
												/>
											</Form.Item>
										</Col>
									</Row>
								</Form>
							</SectionCard>

							{/* Password — a signed-in visitor already has one, and the route refuses the key */}
							{!isAuthenticated && (
								<SectionCard title="Set Your Password">
									<Form layout="vertical">
										<Row gutter={[16, 0]}>
											<Col xs={24} md={12}>
												<Form.Item label={<>Password <span style={{ color: "var(--mg-danger, #ff4d4f)" }}>*</span></>}>
													<Input.Password
														prefix={<LockOutlined style={{ color: "var(--mg-text-disabled, #bfbfbf)" }} />}
														value={formData.password || ""}
														onChange={(e) => handleInputChange("password", e.target.value)}
														minLength={8}
														placeholder="Minimum 8 characters"
													/>
												</Form.Item>
											</Col>
											<Col xs={24} md={12}>
												<Form.Item label={<>Confirm Password <span style={{ color: "var(--mg-danger, #ff4d4f)" }}>*</span></>}>
													<Input.Password
														prefix={<LockOutlined style={{ color: "var(--mg-text-disabled, #bfbfbf)" }} />}
														value={formData.password_confirmation || ""}
														onChange={(e) => handleInputChange("password_confirmation", e.target.value)}
														minLength={8}
														placeholder="Re-enter your password"
													/>
												</Form.Item>
											</Col>
										</Row>
									</Form>
								</SectionCard>
							)}

							{/* Address */}
							<SectionCard title="Address Information">
								<Form layout="vertical">
									<Row gutter={[16, 0]}>
										<Col xs={24}>
											<Form.Item label="Street Address">
												<Input
													value={formData.address || ""}
													onChange={(e) => handleInputChange("address", e.target.value)}
													placeholder="123 Main Street"
												/>
											</Form.Item>
										</Col>
										<Col xs={24} md={10}>
											<Form.Item label="City">
												<Input
													value={formData.city || ""}
													onChange={(e) => handleInputChange("city", e.target.value)}
													placeholder="Melbourne"
												/>
											</Form.Item>
										</Col>
										<Col xs={24} md={6}>
											<Form.Item label="State">
												<Input
													value={formData.state || ""}
													onChange={(e) => handleInputChange("state", e.target.value)}
													placeholder="VIC"
												/>
											</Form.Item>
										</Col>
										<Col xs={24} md={8}>
											<Form.Item label="Postal Code">
												<Input
													value={formData.postal_code || ""}
													onChange={(e) => handleInputChange("postal_code", e.target.value)}
													placeholder="3000"
												/>
											</Form.Item>
										</Col>
										<Col xs={24} md={12}>
											<Form.Item label="Country">
												<Input
													value={formData.country || ""}
													onChange={(e) => handleInputChange("country", e.target.value)}
												/>
											</Form.Item>
										</Col>
									</Row>
								</Form>
							</SectionCard>

							{/* Program selection (new registrations only) */}
							{!isKitRegistered && (
								<SectionCard title="Program Information">
									<Form layout="vertical">
										<Form.Item label="Select Program(s) Purchased">
											<Select
												mode="multiple"
												value={selectedPrograms}
												onChange={handleProgramChange}
												placeholder={loadingPackages ? "Loading programs..." : customPackages.length === 0 ? "No programs available" : "Select Program"}
												loading={loadingPackages}
												disabled={loadingPackages || customPackages.length === 0}
												notFoundContent={loadingPackages ? "Loading..." : "No programs available"}
												style={{ width: "100%" }}
											>
												{customPackages.map((pkg) => (
													<Option key={pkg.id} value={pkg.name}>
														<div style={{ padding: "2px 0" }}>
															<div style={{ fontWeight: 600 }}>{pkg.name}</div>
															{pkg.description && (
																<div style={{ fontSize: 12, color: "var(--mg-text-secondary, #8c8c8c)", marginTop: 2 }}>
																	{pkg.description}
																</div>
															)}
														</div>
													</Option>
												))}
											</Select>
											{!loadingPackages && customPackages.length === 0 && (
												<Text type="danger" style={{ fontSize: 13, marginTop: 8, display: "block" }}>
													⚠️ No test programs are currently available. Please contact support.
												</Text>
											)}
										</Form.Item>
									</Form>
								</SectionCard>
							)}
						</div>

						{/* Submit */}
						<div style={{ textAlign: "center", marginTop: 32 }}>
							<Button
								type="primary"
								onClick={handleSubmit}
								disabled={isSubmitting || !!medicareNumberError || kitRegisteredElsewhere}
								loading={isSubmitting}
								style={{ minWidth: 220 }}
								data-testid="kit-submit-btn"
							>
								{isSubmitting ? "Activating Kit..." : "Activate Kit"}
							</Button>
							{isSubmitting && (
								<div style={{ marginTop: 12 }}>
									<Text type="secondary">
										Please wait while we process your {isKitRegistered ? "registration" : "activation"}…
									</Text>
								</div>
							)}
						</div>
					</>
				)}

				{/* Footer */}
				<div style={{
					textAlign: "center",
					marginTop: 48,
					paddingTop: 24,
					borderTop: "1px solid var(--mg-border, #e8e8e8)",
				}}>
					<Text strong style={{ display: "block" }}>MyGene Pty Ltd</Text>
					<Text type="secondary" style={{ fontSize: 13, display: "block", marginTop: 4 }}>283 Burke Rd, Glen Iris VIC 3146</Text>
					<Text type="secondary" style={{ fontSize: 13 }}>
						Visit us at{" "}
						<a href="https://mygene.com.au" style={{ color: "var(--mg-primary, #1890ff)" }}>
							mygene.com.au
						</a>
					</Text>
				</div>
			</div>

			{/* Forgot username or password */}
			<Modal
				title="Forgot your username or password?"
				open={isForgotOpen}
				onCancel={closeForgotModal}
				footer={null}
				destroyOnClose
			>
				<Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
					Enter the email address on your account and we'll send you a reset link that also states your username.
				</Text>

				{forgotMessage && <Alert type="success" showIcon message={forgotMessage} style={{ marginBottom: 16 }} />}
				{forgotError && <Alert type="error" showIcon message={forgotError} style={{ marginBottom: 16 }} />}

				<Form layout="vertical" onFinish={handleForgotSubmit}>
					<Form.Item label="Email Address">
						<Input
							prefix={<MailOutlined style={{ color: "var(--mg-text-disabled, #bfbfbf)" }} />}
							type="email"
							aria-label="Account email address"
							value={forgotEmail}
							onChange={(e) => setForgotEmail(e.target.value)}
							data-testid="kit-forgot-email"
						/>
					</Form.Item>
					<Button type="primary" htmlType="submit" loading={isSendingReset} disabled={isSendingReset} data-testid="kit-forgot-submit">
						Send reset link
					</Button>
				</Form>
			</Modal>

			{/* Kit instructions modal */}
			<Modal title="DNA Collection Kit Instructions" open={isImagePopupOpen} onCancel={closeImagePopup} footer={null} width={800} destroyOnClose>
				<div style={{ padding: 16 }}>
					<img
						src="/assets/images/Mygene-DNA-Kit-Instructions.jpeg"
						alt="DNA Kit Instructions"
						style={{ width: "100%", height: "auto", borderRadius: 8, marginBottom: 16 }}
					/>
					<Text type="secondary" style={{ display: "block", textAlign: "center" }}>
						Follow these step-by-step instructions to properly collect your DNA sample for accurate results.
					</Text>
				</div>
			</Modal>

			{/* Animations used only on the welcome/loading screen */}
			<style>{`
				@keyframes rk-slide-up {
					from { opacity: 0; transform: translateY(30px); }
					to   { opacity: 1; transform: translateY(0); }
				}
				@keyframes rk-fade-in {
					from { opacity: 0; }
					to   { opacity: 1; }
				}
				.rk-slide-up-delayed {
					animation: rk-slide-up 0.8s ease-out 0.5s forwards;
					opacity: 0;
				}
				.rk-slide-up-super-delayed {
					animation: rk-slide-up 0.8s ease-out 1.2s forwards;
					opacity: 0;
				}
				.rk-fade-in-final {
					animation: rk-fade-in 1s ease-out 2s forwards;
					opacity: 0;
				}
			`}</style>
		</div>
	);
};

export default RegisterKit;
